/* eslint-disable react/prop-types */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Typography,
  Select,
  Option,
  Sheet,
  Snackbar,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import Link from '@mui/joy/Link';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import Filter1Outlined from '@mui/icons-material/Filter1Outlined';
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ViewInArOutlined from '@mui/icons-material/ViewInArOutlined';
import MonitorWeightOutlined from '@mui/icons-material/MonitorWeightOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import LowPriorityOutlined from '@mui/icons-material/LowPriorityOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import NotesOutlined from '@mui/icons-material/NotesOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../../../main/widgets/CustomTextField';
import OmrSymbol from '../../../main/components/OmrSymbol';
import NativeCheckbox from '../../../main/components/NativeCheckbox';
import CustomerOptionList from '../../../main/widgets/CustomersOptionList';
import ShipmentTypeOptionList from '../../../main/widgets/ShipmentTypeOptionList';
import ShipmentUnitOptionList from '../../../main/widgets/ShipmentUnitOptionList';
import PricingPlanOptionList from '../../../main/widgets/PricingPlanOptionList';
import SettlementGroupOptionList from '../../../main/widgets/SettlementGroupOptionList';
import PermissionGate from '../../../main/components/PermissionGate';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import { createPricingRule, getPricingRule, updatePricingRule } from '../../../main/API/pricingRule';
import { createShipmentUnitPricingRule } from '../../../main/API/shipmentUnit';
import DuplicatePricingRuleDialog from './DuplicatePricingRuleDialog';
import PricingRuleFieldHint from './PricingRuleFieldHint';
import { masterApiErrorMessage } from '../../../main/utils/masterApiError';
import {
  buildPricingRulePayload,
  pricingRuleApiValidationFeedback,
  pricingRuleFromApi,
  validatePricingRuleClient,
} from './pricingRuleFormPayload';

const emptyForm = () => ({
  code: '',
  name: '',
  name_ar: '',
  description: '',
  description_ar: '',
  scope: 'base',
  show_in_menu: false,
  menu_label_ar: '',
  menu_label_en: '',
  menu_order: 0,
  plan: null,
  settlement_group: null,
  customer: null,
  customer_group: '',
  tariff_code: '',
  category_code: '',
  shipment_type: null,
  shipment_unit: null,
  applies_to_group: false,
  cbm_from: '',
  cbm_to: '',
  weight_from: '',
  weight_to: '',
  effective_from: '',
  effective_to: '',
  strategy: 'flat_per_shipment',
  total_price: '',
  unit_price: '',
  ceil_step: '',
  percent: '',
  min_line_price: '0',
  threshold_cbm: '',
  priority: 100,
  active: true,
  notes: '',
});

const STRATEGIES = [
  'flat_per_shipment',
  'per_unit_weight',
  'meter_per_line',
  'meter_per_line_ceil',
  'group_total_lookup',
  'flat_addon',
  'percent_addon',
];

/** Document order for scrolling to the first field with a validation / API error */
const PRICING_RULE_FIELD_SCROLL_ORDER = [
  'code',
  'name',
  'name_ar',
  'description',
  'description_ar',
  'scope',
  'plan',
  'settlement_group',
  'show_in_menu',
  'menu_label_en',
  'menu_label_ar',
  'menu_order',
  'customer',
  'customer_group',
  'tariff_code',
  'category_code',
  'shipment_type',
  'shipment_unit',
  'applies_to_group',
  'cbm_from',
  'cbm_to',
  'cbm',
  'weight_from',
  'weight_to',
  'weight',
  'effective_from',
  'effective_to',
  'effective',
  'strategy',
  'total_price',
  'unit_price',
  'ceil_step',
  'percent',
  'min_line_price',
  'threshold_cbm',
  'priority',
  'active',
  'notes',
];

function scrollToFirstPricingRuleFieldError(errors, banner) {
  const errMap = errors && typeof errors === 'object' ? errors : {};
  const hasErr = (k) => Boolean(errMap[k]);

  const tryScroll = (el) => {
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector(
      'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') {
      try {
        focusable.focus({ preventScroll: true });
      } catch (_e) {
        focusable.focus();
      }
    }
    return true;
  };

  for (let i = 0; i < PRICING_RULE_FIELD_SCROLL_ORDER.length; i++) {
    const key = PRICING_RULE_FIELD_SCROLL_ORDER[i];
    if (hasErr(key)) {
      const el = document.getElementById(`pricing-rule-field-${key}`);
      if (tryScroll(el)) return;
    }
  }

  const leftoverKeys = Object.keys(errMap).filter(hasErr);
  for (let j = 0; j < leftoverKeys.length; j++) {
    const key = leftoverKeys[j];
    const el = document.getElementById(`pricing-rule-field-${key}`);
    if (tryScroll(el)) return;
  }

  if (banner) {
    const el = document.getElementById('pricing-rule-save-error');
    if (tryScroll(el)) return;
  }
}

export default function PricingRuleForm({ mode, ruleId, unitContext = null, returnTo = null, planContext = null }) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [success, setSuccess] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [showDuplicatedSnack, setShowDuplicatedSnack] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const pendingScrollToErrorsRef = useRef(false);

  const canSubmit =
    mode === 'create'
      ? hasPermission(PERMISSIONS.PRICING_RULES_CREATE)
      : hasPermission(PERMISSIONS.PRICING_RULES_UPDATE);
  const canDuplicate = mode === 'edit' && hasPermission(PERMISSIONS.PRICING_RULES_CREATE);

  const pricingRuleJustDuplicatedFlag = Boolean(
    location.state && location.state.pricingRuleJustDuplicated
  );

  useEffect(() => {
    if (!pricingRuleJustDuplicatedFlag) return;
    setShowDuplicatedSnack(true);
    const nextState = location.state?.returnTo
      ? { returnTo: location.state.returnTo }
      : {};
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: nextState }
    );
  }, [
    pricingRuleJustDuplicatedFlag,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    location.state?.returnTo,
  ]);

  const lockedUnitContext = unitContext?.id != null ? unitContext : null;
  const lockedPlanContext = planContext?.id != null ? planContext : null;

  useEffect(() => {
    if (mode !== 'create' || !lockedUnitContext?.id) return;
    setForm((prev) => ({
      ...prev,
      shipment_unit: {
        id: lockedUnitContext.id,
        serial_number: lockedUnitContext.serial_number ?? null,
      },
      active: false,
      priority: prev.priority === 100 ? 50 : prev.priority,
    }));
  }, [mode, lockedUnitContext?.id, lockedUnitContext?.serial_number]);

  useEffect(() => {
    if (mode !== 'create' || !lockedPlanContext?.id) return;
    setForm((prev) => ({
      ...prev,
      plan: {
        id: lockedPlanContext.id,
        code: lockedPlanContext.code ?? null,
        name: lockedPlanContext.name ?? null,
      },
      show_in_menu: false,
    }));
  }, [mode, lockedPlanContext?.id, lockedPlanContext?.code, lockedPlanContext?.name]);

  useEffect(() => {
    if (mode !== 'edit' || !ruleId) return;
    if (!hasPermission(PERMISSIONS.PRICING_RULES_VIEW)) {
      setLoadError(t('permissions.noPermission', { defaultValue: 'Permission denied.' }));
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPricingRule(ruleId)
      .then((res) => {
        if (cancelled) return;
        setForm({ ...emptyForm(), ...pricingRuleFromApi(res.data) });
        setLoadError('');
      })
      .catch((e) => {
        if (!cancelled) setLoadError(masterApiErrorMessage(e, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, ruleId, hasPermission, t]);

  useLayoutEffect(() => {
    if (!pendingScrollToErrorsRef.current) return;
    pendingScrollToErrorsRef.current = false;
    scrollToFirstPricingRuleFieldError(fieldErrors, saveError);
  }, [fieldErrors, saveError]);

  const strategyHelp = useMemo(() => form.strategy || 'flat_per_shipment', [form.strategy]);

  const scopeHelperKey = useMemo(() => {
    if (form.scope === 'addon') return 'scopeHelperAddon';
    if (form.scope === 'manual') return 'scopeHelperManual';
    return 'scopeHelperBase';
  }, [form.scope]);

  const pageTitle = lockedUnitContext
    ? t('pricingRules.formAddUnitTitle', {
        serial: lockedUnitContext.serial_number || `#${lockedUnitContext.id}`,
      })
    : mode === 'create'
      ? t('pricingRules.formAddTitle')
      : t('pricingRules.formEditTitle');

  const patch = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const StrategyHintIcon = () => (
    <Tooltip
      title={
        <Box>
          <Typography level="body-xs" component="div" sx={{ mb: 1 }}>
            {t('pricingRules.strategyIgnoredHint')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 1.5,
              rowGap: 0.5,
            }}
          >
            <Typography level="body-xs" fontWeight="lg" component="div">
              {t('pricingRules.strategyFormulaColStrategy')}
            </Typography>
            <Typography level="body-xs" fontWeight="lg" component="div">
              {t('pricingRules.strategyFormulaColFormula')}
            </Typography>
            {STRATEGIES.map((s) => (
              <React.Fragment key={s}>
                <Typography
                  level="body-xs"
                  component="div"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: s === strategyHelp ? 'lg' : 'md',
                    color: s === strategyHelp ? 'primary.plainColor' : 'inherit',
                  }}
                >
                  {s}
                </Typography>
                <Typography
                  level="body-xs"
                  component="div"
                  sx={{
                    fontWeight: s === strategyHelp ? 'lg' : 'md',
                    color: s === strategyHelp ? 'primary.plainColor' : 'inherit',
                  }}
                >
                  {t(`pricingRules.strategyFormula_${s}`)}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      }
      variant="soft"
      placement="top"
      sx={{ maxWidth: { xs: 'min(90vw, 560px)', sm: 560 } }}
    >
      <IconButton
        component="span"
        tabIndex={0}
        size="sm"
        variant="plain"
        sx={{ p: 0.25, minHeight: 'auto', verticalAlign: 'middle' }}
        aria-label={t('widgets.fieldHintMoreInfo')}
        onClick={(e) => e.preventDefault()}
      >
        <InfoOutlined sx={{ fontSize: '1rem', color: 'neutral.500' }} />
      </IconButton>
    </Tooltip>
  );

  const showTotalPrice = ['flat_per_shipment', 'flat_addon', 'group_total_lookup'].includes(
    strategyHelp
  );
  const showUnitPrice = ['per_unit_weight', 'meter_per_line', 'meter_per_line_ceil'].includes(
    strategyHelp
  );
  const showCeil = strategyHelp === 'meter_per_line_ceil';
  const showPercent = strategyHelp === 'percent_addon';

  const totalPriceRequired = showTotalPrice;
  const unitPriceRequired = showUnitPrice;
  const ceilStepRequired = showCeil;
  const percentRequired = showPercent;
  const showGroupStrategyHint = strategyHelp === 'group_total_lookup';
  const minLinePriceRequired = true;

  const handleDuplicateSuccess = (copy) => {
    navigate(`/PricingRules/${copy.id}/Edit`, {
      state: { pricingRuleJustDuplicated: true },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');
    const v = validatePricingRuleClient(form, t, { isCreate: mode === 'create' });
    setFieldErrors(v);
    if (Object.keys(v).length) {
      pendingScrollToErrorsRef.current = true;
      return;
    }
    if (!canSubmit) return;
    const payload = buildPricingRulePayload(form);
    setLoading(true);
    try {
      if (mode === 'create') {
        if (lockedUnitContext?.id) {
          await createShipmentUnitPricingRule(lockedUnitContext.id, payload);
          const dest = returnTo || '/PricingRules';
          navigate(dest, {
            state: {
              pricingRuleJustCreated: true,
              promptUnitRecalc: lockedUnitContext.id,
            },
          });
        } else {
          await createPricingRule(payload);
          navigate(returnTo || '/PricingRules', {
            state: { pricingRuleJustCreated: true },
          });
        }
      } else {
        await updatePricingRule(ruleId, payload);
        setSuccess(true);
      }
    } catch (err) {
      const { fieldErrors: apiFieldErrors, saveError: apiBanner } = pricingRuleApiValidationFeedback(
        err,
        t
      );
      setFieldErrors(apiFieldErrors);
      setSaveError(apiBanner);
      if (Object.keys(apiFieldErrors).length > 0 || apiBanner) {
        pendingScrollToErrorsRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ m: 3 }}>
      <Box mb={2}>
        <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
          <Link color="neutral" href="/PricingRules">
            {t('nav.pricingRules')}
          </Link>
          <Typography>{pageTitle}</Typography>
        </Breadcrumbs>
        <Typography level="h1" textAlign="start">
          {pageTitle}
        </Typography>
      </Box>

      {lockedUnitContext ? (
        <Alert variant="soft" color="primary" sx={{ mb: 2 }}>
          {t('pricingRules.unitContextHint')}
        </Alert>
      ) : null}

      {loadError ? (
        <Typography color="danger" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      ) : null}

      {loading && mode === 'edit' && !form.code ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            gap: 2,
          }}
        >
          <CircularProgress size="lg" />
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            {t('actions.loading')}
          </Typography>
        </Box>
      ) : (
        <Sheet
          variant="outlined"
          sx={{ p: 3, borderRadius: 'md', bgcolor: 'background.surface' }}
          component="form"
          onSubmit={handleSubmit}
        >
          {saveError ? (
            <Typography id="pricing-rule-save-error" color="danger" sx={{ mb: 2 }}>
              {saveError}
            </Typography>
          ) : null}

          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingRules.sectionIdentity')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-code">
              <CustomTextField
                required
                label={t('pricingRules.fieldCode')}
                hintTooltip={t('pricingRules.hintCode')}
                helperText={t('pricingRules.fieldHelperCode')}
                helperIcon={FingerprintOutlined}
                value={form.code}
                onChange={(e) => {
                  const v = e.target.value;
                  patch('code', v);
                  if (fieldErrors.code) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.code;
                      return next;
                    });
                  }
                }}
                disabled={mode === 'edit'}
                fullWidth
              />
              {fieldErrors.code ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.code}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-name">
              <CustomTextField
                required
                label={t('pricingRules.fieldName')}
                hintTooltip={t('pricingRules.hintName')}
                helperText={t('pricingRules.fieldHelperName')}
                helperIcon={LabelOutlined}
                value={form.name}
                onChange={(e) => patch('name', e.target.value)}
                fullWidth
              />
              {fieldErrors.name ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.name}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-name_ar">
              <CustomTextField
                label={t('pricingRules.fieldNameAr')}
                hintTooltip={t('pricingRules.hintNameAr')}
                helperText={t('pricingRules.fieldHelperNameAr')}
                value={form.name_ar}
                onChange={(e) => patch('name_ar', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={6} id="pricing-rule-field-description">
              <FormLabel sx={{ fontWeight: 600, color: '#555', mb: 0.75, fontSize: '14px' }}>
                {t('pricingRules.fieldDescription')}
              </FormLabel>
              <FormControl sx={{ width: '100%' }}>
                <textarea
                  style={{
                    width: '100%',
                    minHeight: 72,
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    fontFamily: 'inherit',
                    backgroundColor: '#f7f9fc',
                  }}
                  value={form.description}
                  onChange={(e) => patch('description', e.target.value)}
                />
              </FormControl>
              <PricingRuleFieldHint icon={NotesOutlined}>
                {t('pricingRules.fieldHelperDescription')}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} md={6} id="pricing-rule-field-description_ar">
              <FormLabel sx={{ fontWeight: 600, color: '#555', mb: 0.75, fontSize: '14px' }}>
                {t('pricingRules.fieldDescriptionAr')}
              </FormLabel>
              <textarea
                style={{
                  width: '100%',
                  minHeight: 72,
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #ccc',
                  fontFamily: 'inherit',
                  backgroundColor: '#f7f9fc',
                }}
                value={form.description_ar}
                onChange={(e) => patch('description_ar', e.target.value)}
              />
              <PricingRuleFieldHint icon={NotesOutlined}>
                {t('pricingRules.fieldHelperDescriptionAr')}
              </PricingRuleFieldHint>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingRules.sectionDisplay')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={4} id="pricing-rule-field-scope">
              <FormLabel sx={{ fontWeight: 600, color: '#555', mb: 0.75, fontSize: '14px' }}>
                {t('pricingRules.fieldScope')} *
              </FormLabel>
              <Select
                value={form.scope}
                onChange={(_, v) => patch('scope', v)}
                sx={{ width: '100%' }}
              >
                <Option value="base">
                  <Filter1Outlined sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  base
                </Option>
                <Option value="addon">
                  <AddCircleOutlineOutlined sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  addon
                </Option>
                <Option value="manual">
                  <TouchAppOutlined sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  manual
                </Option>
              </Select>
              <PricingRuleFieldHint
                icon={
                  form.scope === 'addon'
                    ? AddCircleOutlineOutlined
                    : form.scope === 'manual'
                      ? TouchAppOutlined
                      : Filter1Outlined
                }
              >
                {t(`pricingRules.${scopeHelperKey}`)}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} sm={8} id="pricing-rule-field-plan">
              <PricingPlanOptionList
                key={(form.plan && form.plan.id) || 'none'}
                label={t('pricingRules.fieldPlan')}
                defaultValue={form.plan}
                disabled={Boolean(lockedPlanContext?.id)}
                required
                onChange={(_, value) => {
                  patch('plan', value);
                  if (fieldErrors.plan) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.plan;
                      return next;
                    });
                  }
                }}
                errorMessage={fieldErrors.plan}
                activeOnly={false}
              />
              <PricingRuleFieldHint icon={MenuBookOutlined}>
                {t('pricingRules.fieldHelperPlan')}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} sm={8} id="pricing-rule-field-settlement_group">
              <SettlementGroupOptionList
                key={(form.settlement_group && form.settlement_group.id) || 'none'}
                label={t('pricingRules.fieldSettlementGroup')}
                defaultValue={form.settlement_group}
                required
                onChange={(_, value) => patch('settlement_group', value)}
                errorMessage={fieldErrors.settlement_group}
                activeOnly={false}
              />
              <PricingRuleFieldHint icon={AccountBalanceWalletOutlined}>
                {t('pricingRules.fieldHelperSettlementGroup')}
              </PricingRuleFieldHint>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingRules.sectionMatch')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} id="pricing-rule-field-customer">
              <CustomerOptionList
                key={(form.customer && form.customer.id) || 'none'}
                label={t('pricingRules.fieldCustomer')}
                defaultValue={form.customer}
                onChange={(_, value) => patch('customer', value)}
              />
              <PricingRuleFieldHint icon={PersonOutlineOutlined}>
                {t('pricingRules.fieldHelperCustomer')}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-customer_group">
              <CustomTextField
                label={t('pricingRules.fieldCustomerGroup')}
                hintTooltip={t('pricingRules.hintCustomerGroup')}
                helperText={t('pricingRules.fieldHelperCustomerGroup')}
                helperIcon={GroupsOutlined}
                value={form.customer_group}
                onChange={(e) => patch('customer_group', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-tariff_code">
              <CustomTextField
                label={t('pricingRules.fieldTariffCode')}
                hintTooltip={t('pricingRules.hintTariffCode')}
                helperText={t('pricingRules.fieldHelperTariff')}
                helperIcon={LocalOfferOutlined}
                value={form.tariff_code}
                onChange={(e) => patch('tariff_code', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-category_code">
              <CustomTextField
                label={t('pricingRules.fieldCategoryCode')}
                hintTooltip={t('pricingRules.hintCategoryCode')}
                helperText={t('pricingRules.fieldHelperCategory')}
                helperIcon={CategoryOutlined}
                value={form.category_code}
                onChange={(e) => patch('category_code', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-shipment_type">
              <ShipmentTypeOptionList
                label={t('pricingRules.fieldShipmentType')}
                required={false}
                defaultValue={form.shipment_type}
                onChange={(_, value) => patch('shipment_type', value)}
                slotProps={{ root: { sx: { marginBottom: 0 } } }}
              />
              <PricingRuleFieldHint icon={LocalShippingOutlined}>
                {t('pricingRules.fieldHelperShipmentType')}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} sm={6} md={4} id="pricing-rule-field-shipment_unit">
              {lockedUnitContext ? (
                <CustomTextField
                  label={t('pricingRules.fieldShipmentUnit')}
                  hintTooltip={t('pricingRules.hintShipmentUnit')}
                  helperText={t('pricingRules.fieldHelperShipmentUnit')}
                  helperIcon={Inventory2Outlined}
                  value={
                    lockedUnitContext.serial_number
                      ? `${lockedUnitContext.serial_number} (#${lockedUnitContext.id})`
                      : `#${lockedUnitContext.id}`
                  }
                  disabled
                  fullWidth
                />
              ) : (
                <>
                  <ShipmentUnitOptionList
                    label={t('pricingRules.fieldShipmentUnit')}
                    defaultValue={form.shipment_unit}
                    onChange={(_, value) => patch('shipment_unit', value)}
                  />
                  <PricingRuleFieldHint icon={Inventory2Outlined}>
                    {t('pricingRules.fieldHelperShipmentUnit')}
                  </PricingRuleFieldHint>
                </>
              )}
            </Grid>
            <Grid xs={12} id="pricing-rule-field-applies_to_group">
              <NativeCheckbox
                checked={form.applies_to_group}
                onChange={(e) => patch('applies_to_group', e.target.checked)}
                label={t('pricingRules.fieldAppliesToGroup')}
              />
              <PricingRuleFieldHint icon={GroupsOutlined}>
                {t('pricingRules.fieldHelperAppliesToGroup')}
              </PricingRuleFieldHint>
              {showGroupStrategyHint && !form.applies_to_group ? (
                <PricingRuleFieldHint icon={WarningAmberOutlined} color="warning.700">
                  {t('pricingRules.appliesToGroupRecommend')}
                </PricingRuleFieldHint>
              ) : null}
            </Grid>
            <Grid xs={6} md={3} id="pricing-rule-field-cbm_from">
              <CustomTextField
                label={t('pricingRules.fieldCbmFrom')}
                hintTooltip={t('pricingRules.hintCbmFrom')}
                helperText={t('pricingRules.fieldHelperCbmFrom')}
                helperIcon={ViewInArOutlined}
                value={form.cbm_from}
                onChange={(e) => patch('cbm_from', e.target.value)}
                fullWidth
              />
              {fieldErrors.cbm_from ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.cbm_from}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={6} md={3} id="pricing-rule-field-cbm_to">
              <CustomTextField
                label={t('pricingRules.fieldCbmTo')}
                hintTooltip={t('pricingRules.hintCbmTo')}
                helperText={t('pricingRules.fieldHelperCbmTo')}
                helperIcon={ViewInArOutlined}
                value={form.cbm_to}
                onChange={(e) => patch('cbm_to', e.target.value)}
                fullWidth
              />
              {fieldErrors.cbm_to ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.cbm_to}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={6} md={3} id="pricing-rule-field-weight_from">
              <CustomTextField
                label={t('pricingRules.fieldWeightFrom')}
                hintTooltip={t('pricingRules.hintWeightFrom')}
                helperText={t('pricingRules.fieldHelperWeightFrom')}
                helperIcon={MonitorWeightOutlined}
                value={form.weight_from}
                onChange={(e) => patch('weight_from', e.target.value)}
                fullWidth
              />
              {fieldErrors.weight_from ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.weight_from}</FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={6} md={3} id="pricing-rule-field-weight_to">
              <CustomTextField
                label={t('pricingRules.fieldWeightTo')}
                hintTooltip={t('pricingRules.hintWeightTo')}
                helperText={t('pricingRules.fieldHelperWeightTo')}
                helperIcon={MonitorWeightOutlined}
                value={form.weight_to}
                onChange={(e) => patch('weight_to', e.target.value)}
                fullWidth
              />
              {fieldErrors.weight_to ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.weight_to}</FormHelperText>
              ) : null}
            </Grid>
            {fieldErrors.weight ? (
              <Grid xs={12} id="pricing-rule-field-weight">
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.weight}</FormHelperText>
              </Grid>
            ) : null}
            {fieldErrors.cbm ? (
              <Grid xs={12} id="pricing-rule-field-cbm">
                <FormHelperText sx={{ color: 'danger.500' }}>{fieldErrors.cbm}</FormHelperText>
              </Grid>
            ) : null}
            <Grid xs={12} md={6} id="pricing-rule-field-effective_from">
              <CustomTextField
                label={t('pricingRules.fieldEffectiveFrom')}
                hintTooltip={t('pricingRules.hintEffectiveFrom')}
                helperText={t('pricingRules.fieldHelperEffectiveFrom')}
                helperIcon={EventOutlined}
                type="datetime-local"
                value={form.effective_from}
                onChange={(e) => patch('effective_from', e.target.value)}
                slotProps={{
                  input: {
                    type: 'datetime-local',
                    step: 60,
                  },
                }}
                fullWidth
              />
              {fieldErrors.effective_from ? (
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {fieldErrors.effective_from}
                </FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={6} id="pricing-rule-field-effective_to">
              <CustomTextField
                label={t('pricingRules.fieldEffectiveTo')}
                hintTooltip={t('pricingRules.hintEffectiveTo')}
                helperText={t('pricingRules.fieldHelperEffectiveTo')}
                helperIcon={EventOutlined}
                type="datetime-local"
                value={form.effective_to}
                onChange={(e) => patch('effective_to', e.target.value)}
                slotProps={{
                  input: {
                    type: 'datetime-local',
                    step: 60,
                  },
                }}
                fullWidth
              />
              {fieldErrors.effective_to ? (
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {fieldErrors.effective_to}
                </FormHelperText>
              ) : null}
            </Grid>
            {fieldErrors.effective ? (
              <Grid xs={12} id="pricing-rule-field-effective">
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {fieldErrors.effective}
                </FormHelperText>
              </Grid>
            ) : null}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingRules.sectionStrategy')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={6} id="pricing-rule-field-strategy">
              <FormLabel sx={{ fontWeight: 600, color: '#555', mb: 0.75, fontSize: '14px' }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {t('pricingRules.fieldStrategy')} *
                  <StrategyHintIcon />
                </Box>
              </FormLabel>
              <Select
                value={form.strategy}
                onChange={(_, v) => patch('strategy', v)}
                sx={{ width: '100%' }}
              >
                {STRATEGIES.map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
              <PricingRuleFieldHint icon={CalculateOutlined}>
                {t('pricingRules.fieldHelperStrategy')}
              </PricingRuleFieldHint>
            </Grid>

            {showTotalPrice ? (
              <Grid xs={12} md={6} id="pricing-rule-field-total_price">
                <CustomTextField
                  required={totalPriceRequired}
                  label={t('pricingRules.fieldTotalPrice')}
                  hintTooltip={t('pricingRules.hintTotalPrice')}
                  helperText={t('pricingRules.fieldHelperTotalPrice')}
                  value={form.total_price}
                  onChange={(e) => patch('total_price', e.target.value)}
                  endDecorator={<OmrSymbol height={16} />}
                  fullWidth
                />
                {fieldErrors.total_price ? (
                  <FormHelperText sx={{ color: 'danger.500' }}>
                    {fieldErrors.total_price}
                  </FormHelperText>
                ) : null}
              </Grid>
            ) : null}

            {showUnitPrice ? (
              <Grid xs={12} md={6} id="pricing-rule-field-unit_price">
                <CustomTextField
                  required={unitPriceRequired}
                  label={t('pricingRules.fieldUnitPrice')}
                  hintTooltip={t('pricingRules.hintUnitPrice')}
                  helperText={t('pricingRules.fieldHelperUnitPrice')}
                  value={form.unit_price}
                  onChange={(e) => patch('unit_price', e.target.value)}
                  endDecorator={<OmrSymbol height={16} />}
                  fullWidth
                />
                {fieldErrors.unit_price ? (
                  <FormHelperText sx={{ color: 'danger.500' }}>
                    {fieldErrors.unit_price}
                  </FormHelperText>
                ) : null}
              </Grid>
            ) : null}

            {showCeil ? (
              <Grid xs={12} md={6} id="pricing-rule-field-ceil_step">
                <CustomTextField
                  required={ceilStepRequired}
                  label={t('pricingRules.fieldCeilStep')}
                  hintTooltip={t('pricingRules.hintCeilStep')}
                  helperText={t('pricingRules.fieldHelperCeilStep')}
                  value={form.ceil_step}
                  onChange={(e) => patch('ceil_step', e.target.value)}
                  fullWidth
                />
                {fieldErrors.ceil_step ? (
                  <FormHelperText sx={{ color: 'danger.500' }}>
                    {fieldErrors.ceil_step}
                  </FormHelperText>
                ) : null}
              </Grid>
            ) : null}

            {showPercent ? (
              <Grid xs={12} md={6} id="pricing-rule-field-percent">
                <CustomTextField
                  required={percentRequired}
                  label={t('pricingRules.fieldPercent')}
                  hintTooltip={t('pricingRules.hintPercent')}
                  helperText={t('pricingRules.fieldHelperPercent')}
                  value={form.percent}
                  onChange={(e) => patch('percent', e.target.value)}
                  fullWidth
                />
                {fieldErrors.percent ? (
                  <FormHelperText sx={{ color: 'danger.500' }}>
                    {fieldErrors.percent}
                  </FormHelperText>
                ) : null}
              </Grid>
            ) : null}

            <Grid xs={12} md={6} id="pricing-rule-field-min_line_price">
              <CustomTextField
                required={minLinePriceRequired}
                label={t('pricingRules.fieldMinLinePrice')}
                hintTooltip={t('pricingRules.hintMinLinePrice')}
                helperText={t('pricingRules.fieldHelperMinLinePrice')}
                value={form.min_line_price}
                onChange={(e) => patch('min_line_price', e.target.value)}
                endDecorator={<OmrSymbol height={16} />}
                fullWidth
              />
              {fieldErrors.min_line_price ? (
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {fieldErrors.min_line_price}
                </FormHelperText>
              ) : null}
            </Grid>
            <Grid xs={12} md={6} id="pricing-rule-field-threshold_cbm">
              <CustomTextField
                label={t('pricingRules.fieldThresholdCbm')}
                hintTooltip={t('pricingRules.hintThresholdCbm')}
                helperText={t('pricingRules.fieldHelperThresholdCbm')}
                value={form.threshold_cbm}
                onChange={(e) => patch('threshold_cbm', e.target.value)}
                fullWidth
              />
              {fieldErrors.threshold_cbm ? (
                <FormHelperText sx={{ color: 'danger.500' }}>
                  {fieldErrors.threshold_cbm}
                </FormHelperText>
              ) : null}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t('pricingRules.sectionMeta')}
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} md={4} id="pricing-rule-field-priority">
              <CustomTextField
                label={t('pricingRules.fieldPriority')}
                hintTooltip={t('pricingRules.hintPriority')}
                helperText={t('pricingRules.fieldHelperPriority')}
                helperIcon={LowPriorityOutlined}
                type="number"
                value={form.priority}
                onChange={(e) => patch('priority', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid xs={12} md={8} id="pricing-rule-field-active">
              <NativeCheckbox
                checked={form.active}
                onChange={(e) => patch('active', e.target.checked)}
                label={t('pricingRules.fieldActive')}
              />
              <PricingRuleFieldHint icon={ToggleOnOutlined}>
                {t('pricingRules.fieldHelperActive')}
              </PricingRuleFieldHint>
            </Grid>
            <Grid xs={12} id="pricing-rule-field-notes">
              <FormLabel sx={{ fontWeight: 600, color: '#555', mb: 0.75, fontSize: '14px' }}>
                {t('pricingRules.fieldNotes')}
              </FormLabel>
              <textarea
                style={{
                  width: '100%',
                  minHeight: 72,
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #ccc',
                  fontFamily: 'inherit',
                  backgroundColor: '#f7f9fc',
                }}
                value={form.notes}
                onChange={(e) => patch('notes', e.target.value)}
              />
              <PricingRuleFieldHint icon={NotesOutlined}>
                {t('pricingRules.fieldHelperNotes')}
              </PricingRuleFieldHint>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <PermissionGate
              permission={
                mode === 'create'
                  ? PERMISSIONS.PRICING_RULES_CREATE
                  : PERMISSIONS.PRICING_RULES_UPDATE
              }
            >
              <Button type="submit" loading={loading}>
                {mode === 'create' ? t('actions.submit') : t('serviceManager.buttonUpdate')}
              </Button>
            </PermissionGate>
            {canDuplicate ? (
              <Button
                type="button"
                variant="outlined"
                color="neutral"
                onClick={() => setDuplicateDialogOpen(true)}
              >
                {t('pricingRules.buttonDuplicate')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="plain"
              onClick={() => navigate(returnTo || '/PricingRules')}
            >
              {returnTo ? t('actions.back') : t('pricingRules.backToList')}
            </Button>
          </Box>
        </Sheet>
      )}

      <Snackbar
        open={success}
        autoHideDuration={3500}
        onClose={() => setSuccess(false)}
        color="success"
        variant="soft"
      >
        {t('pricingRules.snackbarUpdated')}
      </Snackbar>
      <Snackbar
        open={showDuplicatedSnack}
        autoHideDuration={4500}
        onClose={() => setShowDuplicatedSnack(false)}
        color="success"
        variant="soft"
      >
        {t('pricingRules.snackbarDuplicated')}
      </Snackbar>

      <DuplicatePricingRuleDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        sourceRule={
          canDuplicate
            ? { id: ruleId, code: form.code, name: form.name }
            : null
        }
        onSuccess={handleDuplicateSuccess}
      />
    </Box>
  );
}
